import { OrgChartEmployee } from '../types/orgChart.types';

/**
 * Normalizes a string for loose file-name matching: lowercased, with runs of
 * whitespace, underscores, hyphens, and dots collapsed to a single space and the
 * result trimmed. This lets `John_Doe`, `john doe`, and `John-Doe` all compare equal,
 * and lets a login embedded in `jdoe_card.pdf` be found regardless of separators.
 */
const normalize = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\s_.-]+/g, ' ')
    .trim();

/**
 * Decides whether a WorkDocs document (identified by its file name) belongs to a given
 * employee. A document matches when its normalized name CONTAINS either the employee's
 * login or their `firstName lastName`, so both alias-based names (`jdoe_card.pdf`) and
 * full-name-based names (the generated `John_Doe_Talent_Profile_2025_Q3.pdf`) are
 * recognized. Matching is case-insensitive and tolerant of spaces/underscores/hyphens/dots.
 *
 * Pure and React-free.
 */
export function documentMatchesEmployee(docName: string, employee: OrgChartEmployee): boolean {
  const haystack = normalize(docName);
  if (!haystack) return false;

  const login = normalize(employee.login);
  if (login && haystack.includes(login)) return true;

  const fullName = normalize(`${employee.firstName} ${employee.lastName}`);
  // Guard against an empty/space-only full name matching everything.
  if (fullName && haystack.includes(fullName)) return true;

  return false;
}
